/**
 * Component showing the parameters of Stereo To Spatial on the home page.
 *
 * Author: Bounlith Sirivong <bounlith.sirivong@ircamamplify.com>
 * Copyright © Ircam Amplify 2025.
 */

import { useState } from 'react'
import ModuleConfigList from '../../ModuleConfig/ModuleConfigList'
import { ModuleKey } from '../../ModuleConfig/ModuleInfo'
import { ModuleParam, ModuleParamOption } from '../../ModuleConfig/ModuleParam'
import { HomeModuleQuestionMark } from '../HomeModuleQuestionMark'
import '../styles/homemodule.css'
import './styles/stereo-to-spatial-param.css'

export interface StereoToSpatialParamProps {
  updateModuleParamList: (newParam: Record<string, any>) => void
}

type StereoToSpatialParamList = {
  paramDef: ModuleParam<any> | null
  name: string
  valueList: ModuleParamOption[]
  default: any
  defaultValue: string
}

enum StereoToSpatialParamIndex {
  Preset,
  SurroundFormat,
}

function createStereoToSpatialParamList(
  configParamIndex: StereoToSpatialParamIndex
): StereoToSpatialParamList {
  const config = ModuleConfigList[ModuleKey.StereoToSpatial]
  const paramDef = config.paramList?.[configParamIndex] || null
  const optionList = paramDef?.options || []

  const defaultOption =
    optionList.find((option) => option.defaultValue) || optionList[0]

  return {
    paramDef: paramDef,
    name: paramDef?.name ?? 'param',
    valueList: paramDef?.options ?? [],
    default: defaultOption,
    defaultValue: defaultOption?.value ?? '',
  }
}

/**
 * Component displaying the selection of the parameters for Stereo To Spatial.
 */
const StereoToSpatialParamComp = (props: StereoToSpatialParamProps) => {
  const presetList = createStereoToSpatialParamList(
    StereoToSpatialParamIndex.Preset
  )
  const formatList = createStereoToSpatialParamList(
    StereoToSpatialParamIndex.SurroundFormat
  )

  // Store the selected value in state
  const [selectedPreset, setSelectedPreset] = useState(presetList.defaultValue)
  const [selectedFormat, setSelectedFormat] = useState(formatList.defaultValue)

  function presetChanged(e: any) {
    const value = e.target.value
    setSelectedPreset(value)
    props.updateModuleParamList({ [presetList.name]: value })
  }

  function formatChanged(e: any) {
    const value = e.target.value
    setSelectedFormat(value)
    props.updateModuleParamList({ [formatList.name]: value })
  }

  return (
    <div className="param-container stereo-to-spatial-param">
      {/* ────────────────────────────────────────────────
          Preset selector
          ────────────────────────────────────────────────*/}
      <div className="param-comp stereo-to-spatial-preset">
        <div className="param-label">
          <div>Preset</div>
          <HomeModuleQuestionMark helpMessage="Create a greater or more subtle effect" />
        </div>

        <select value={selectedPreset} onChange={presetChanged}>
          {presetList?.valueList?.map(
            (option: { value: string; title: string }, index: number) => (
              <option key={index} value={option.value}>
                {/* Remove index from title */}
                {option.title.replace(/^\d+\s+/, '')}
              </option>
            )
          )}
        </select>
      </div>

      {/* ────────────────────────────────────────────────
          Surround format selector
          ────────────────────────────────────────────────*/}
      <div
        className="param-comp stereo-to-spatial-surroud-format"
        style={{ marginLeft: 'auto' }}
      >
        <div className="param-label stereo-to-spatial-format">
          <div>Surround format</div>
          <HomeModuleQuestionMark helpMessage="Format of the surround output" />
        </div>

        <select value={selectedFormat} onChange={formatChanged}>
          {formatList?.valueList?.map(
            (option: { value: string; title: string }, index: number) => (
              <option key={index} value={option.value}>
                {option.title}
              </option>
            )
          )}
        </select>
      </div>
    </div>
  )
}

export default StereoToSpatialParamComp
